import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { appApi } from "@/api";
import BrandWordmark from "@/components/brand/BrandWordmark";
import { BRAND_NAME, BRAND_SUBTITLE } from "@/config/brand";
import { env } from "@/config/env";
import { auth } from "@/lib/auth";
import { session } from "@/lib/session";
import type { LoginAccountResult } from "@/types/api";
import loginLogo from "@/assets/login-logo.png";

const buildCallbackUrl = (target: string) => {
  const current = new URL(window.location.href);
  current.searchParams.delete("code");
  current.searchParams.delete("state");
  current.searchParams.set("redirect", target);
  return current.toString();
};

const BIND_REQUIRED_REDIRECT_PREFIXES = ["/profile", "/orders", "/assistant", "/therapist", "/therapist-detail"];

const getPathname = (target: string) => {
  try {
    return new URL(target, window.location.origin).pathname;
  } catch {
    return target.split(/[?#]/)[0] || "/";
  }
};

const requiresBoundMobile = (target: string) => {
  const pathname = getPathname(target);
  return BIND_REQUIRED_REDIRECT_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
};

const getLoginMobile = (payload: LoginAccountResult) =>
  String(payload.mobile || payload.user_info?.mobile || "").trim();

const resolvePostLoginRedirect = (payload: LoginAccountResult, target: string) => {
  if (!getLoginMobile(payload) && requiresBoundMobile(target)) {
    return "/";
  }
  return target;
};

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [agreed, setAgreed] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPhoneLogin, setShowPhoneLogin] = useState(false);
  const [mobile, setMobile] = useState(env.debugLoginMobile || "13800138000");
  const [captcha, setCaptcha] = useState(env.debugLoginCaptcha || "123456");
  const [brandName, setBrandName] = useState(BRAND_NAME);

  const redirect = useMemo(() => searchParams.get("redirect") || "/", [searchParams]);
  const code = searchParams.get("code") || "";
  const canUsePhoneLogin = env.enableTestPhoneLogin;

  useEffect(() => {
    appApi
      .getOpenConfig()
      .then((config) => {
        const remoteBrandName = String(config?.shop_name || "").trim();
        if (remoteBrandName) {
          setBrandName(remoteBrandName);
        }
      })
      .catch(() => {
        // 使用本地品牌兜底，避免配置接口异常阻塞登录页展示。
      });
  }, []);

  useEffect(() => {
    if (code || !auth.getToken()) {
      return;
    }
    navigate(redirect, { replace: true });
  }, [code, navigate, redirect]);

  useEffect(() => {
    let cancelled = false;
    if (!code) {
      return;
    }

    const runWechatLogin = async () => {
      setLoading(true);
      setError("");
      try {
        auth.clearSession();
        const result = await appApi.oaLogin({ code });
        if (cancelled) {
          return;
        }
        session.saveLogin(result);
        navigate(resolvePostLoginRedirect(result, redirect), { replace: true });
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError instanceof Error ? requestError.message : "微信登录失败");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void runWechatLogin();

    return () => {
      cancelled = true;
    };
  }, [code, navigate, redirect]);

  const openAgreement = (type: "service" | "privacy") => {
    navigate(`/agreement?type=${type}`);
  };

  const handleWechatLogin = async () => {
    if (!agreed) {
      setError("请先阅读并同意相关协议后再继续登录");
      return;
    }

    setLoading(true);
    setError("");

    try {
      if (env.isDev && env.debugLoginMobile && env.debugLoginCaptcha) {
        await session.bootstrap();
        const userInfo = auth.getUserInfo();
        navigate(userInfo?.mobile || !requiresBoundMobile(redirect) ? redirect : "/", { replace: true });
        return;
      }

      const result = await appApi.getWechatCodeUrl({
        url: buildCallbackUrl(redirect),
        scope: "snsapi_userinfo",
        state: "AT_HOME_H5",
      });
      window.location.href = result.url;
    } catch (requestError) {
      setLoading(false);
      setError(requestError instanceof Error ? requestError.message : "获取微信授权地址失败");
    }
  };

  const handlePhoneLogin = async () => {
    if (!canUsePhoneLogin) {
      return;
    }
    if (!agreed) {
      setError("请先阅读并同意相关协议后再继续登录");
      return;
    }
    if (!/^1\d{10}$/.test(mobile.trim())) {
      setError("请输入正确的手机号");
      return;
    }
    if (!/^\d{6}$/.test(captcha.trim())) {
      setError("请输入任意 6 位验证码");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const result = await appApi.loginAccount({
        mobile: mobile.trim(),
        captcha: captcha.trim(),
        terminal: env.debugLoginTerminal,
      });
      session.saveLogin(result);
      navigate(resolvePostLoginRedirect(result, redirect), { replace: true });
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "手机号登录失败");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="mx-auto flex min-h-screen max-w-[480px] flex-col px-5 pb-8"
      style={{
        minHeight: "100dvh",
        paddingTop: "calc(env(safe-area-inset-top, 0px) + 14px)",
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
        background:
          "radial-gradient(circle at 20% 10%, rgba(250, 188, 110, 0.18), transparent 24%), radial-gradient(circle at 82% 12%, rgba(245, 171, 87, 0.16), transparent 20%), linear-gradient(180deg, #FFF9F3 0%, #FFFCF8 45%, #FFF7EE 100%)",
      }}
    >
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/", { replace: true }))}
          className="flex h-11 w-11 items-center justify-center rounded-full"
          style={{ background: "rgba(255,255,255,0.78)", boxShadow: "0 10px 24px rgba(222, 181, 124, 0.12)" }}
        >
          <i className="ri-close-line text-[26px] text-[#6B7280]" />
        </button>

        {canUsePhoneLogin ? (
          <button
            type="button"
            onClick={() => setShowPhoneLogin((prev) => !prev)}
            className="flex h-9 w-9 items-center justify-center rounded-full transition"
            style={{ background: "rgba(255,255,255,0.62)", boxShadow: "0 8px 18px rgba(222, 181, 124, 0.10)" }}
            aria-label={showPhoneLogin ? "隐藏测试登录入口" : "打开测试登录入口"}
            title={showPhoneLogin ? "隐藏测试登录入口" : "打开测试登录入口"}
          >
            <i className={`${showPhoneLogin ? "ri-close-line" : "ri-smartphone-line"} text-[18px] text-[#C58A54]`} />
          </button>
        ) : (
          <div className="w-11" />
        )}
      </div>

      <div className="flex flex-1 flex-col justify-center">
        <div className="pt-4 text-center">
          <div className="flex justify-center">
            <img
              src={loginLogo}
              alt={`${brandName} logo`}
              className="h-[116px] w-[116px] rounded-[30px] object-cover"
              style={{ boxShadow: "0 22px 40px rgba(245, 128, 36, 0.14)" }}
            />
          </div>

          <div className="mt-6">
            <BrandWordmark name={brandName} subtitle={BRAND_SUBTITLE} />
          </div>

          <div className="mx-auto mt-8 max-w-[320px]">
            <h1 className="text-[20px] font-black leading-tight text-[#111827]">你好，欢迎来到 {brandName}</h1>
            <p className="mt-3 text-[14px] leading-7 text-[#B8AA9A]">微信授权登录后即可继续使用到家服务。</p>
          </div>
        </div>

        <div className="mt-10">
          {error ? (
            <div
              className="mb-4 rounded-[22px] px-4 py-3 text-[15px]"
              style={{ background: "#FFF4EC", color: "#D94A00", border: "1px solid #FFD8BF" }}
            >
              {error}
            </div>
          ) : null}

          {canUsePhoneLogin && showPhoneLogin ? (
            <div
              className="mb-5 rounded-[28px] p-4"
              style={{ background: "rgba(255,255,255,0.82)", border: "1px solid #F2E5D8", boxShadow: "0 12px 28px rgba(222, 181, 124, 0.10)" }}
            >
              <div className="mb-3 flex items-center justify-between">
                <p className="text-[15px] font-black text-[#111827]">测试环境手机号登录</p>
                <span className="rounded-full bg-[#FFF0E6] px-2.5 py-1 text-[11px] font-bold text-[#F1722A]">TEST</span>
              </div>

              <div className="space-y-3">
                <div
                  className="flex items-center gap-3 rounded-[20px] px-4 py-3"
                  style={{ background: "#FFF9F4", border: "1px solid #F3E7DB" }}
                >
                  <i className="ri-smartphone-line text-[18px] text-[#F08A3D]" />
                  <input
                    value={mobile}
                    onChange={(event) => setMobile(event.target.value)}
                    inputMode="numeric"
                    maxLength={11}
                    placeholder="请输入手机号"
                    className="flex-1 bg-transparent text-[15px] text-[#111827] outline-none placeholder:text-[#C6B8A9]"
                  />
                </div>

                <div
                  className="flex items-center gap-3 rounded-[20px] px-4 py-3"
                  style={{ background: "#FFF9F4", border: "1px solid #F3E7DB" }}
                >
                  <i className="ri-shield-keyhole-line text-[18px] text-[#F08A3D]" />
                  <input
                    value={captcha}
                    onChange={(event) => setCaptcha(event.target.value)}
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="请输入任意 6 位验证码"
                    className="flex-1 bg-transparent text-[15px] text-[#111827] outline-none placeholder:text-[#C6B8A9]"
                  />
                </div>
              </div>

              <p className="mt-3 text-[12px] leading-6 text-[#B8AA9A]">
                仅测试环境开放。验证码无需真实短信，输入任意 6 位数字即可。
              </p>

              <button
                type="button"
                onClick={() => void handlePhoneLogin()}
                disabled={loading}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-[16px] font-black text-white"
                style={{
                  background: loading ? "#F0B37D" : "linear-gradient(90deg, #F7AF62 0%, #F1722A 100%)",
                  boxShadow: "0 14px 28px rgba(241, 114, 42, 0.18)",
                }}
              >
                <i className="ri-login-circle-line text-[20px]" />
                <span>{loading ? "登录处理中..." : "手机号验证码登录"}</span>
              </button>
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => void handleWechatLogin()}
            disabled={loading}
            className="flex w-full items-center justify-center gap-3 rounded-full py-4 text-[18px] font-black text-white"
            style={{
              background: loading ? "#F0B37D" : "linear-gradient(90deg, #F7AF62 0%, #F1722A 100%)",
              boxShadow: "0 18px 36px rgba(241, 114, 42, 0.24)",
            }}
          >
            <i className="ri-wechat-fill text-[28px]" />
            <span>{loading ? "登录处理中..." : "微信一键登录"}</span>
          </button>

          <div className="mt-6 flex items-start gap-3 text-left">
            <button
              type="button"
              onClick={() => setAgreed((prev) => !prev)}
              className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border"
              style={{
                borderColor: agreed ? "#F08A3D" : "#D8CEC3",
                background: agreed ? "#FFF0E6" : "transparent",
                color: agreed ? "#F1722A" : "#D8CEC3",
              }}
            >
              {agreed ? <i className="ri-check-line text-lg" /> : null}
            </button>

            <span className="min-w-0 flex-1 text-[13px] leading-6 text-[#A99A8A]">
              <span className="block">
                <span>我已阅读并同意 </span>
                <button type="button" onClick={() => openAgreement("service")} className="text-[#F1722A]">
                  《用户服务协议》
                </button>
                <span className="whitespace-nowrap">
                  <span> 和 </span>
                  <button type="button" onClick={() => openAgreement("privacy")} className="text-[#F1722A]">
                    《隐私政策》
                  </button>
                </span>
              </span>
            </span>
          </div>
        </div>
      </div>

      <div className="pt-6 text-center">
        <div className="mx-auto flex max-w-[240px] items-center gap-4">
          <span className="h-px flex-1 bg-[#EFE4D8]" />
          <span className="text-[#D8C3AC]">
            <i className="ri-sparkling-2-line text-base" />
          </span>
          <span className="h-px flex-1 bg-[#EFE4D8]" />
        </div>

        <div className="mt-5 text-[12px] tracking-[0.08em] text-[#C9B8A3]">{brandName} · 到家服务用户端</div>
      </div>
    </div>
  );
}
